import { kybService } from "@services/apis/Kyb";
import React, { useState } from "react";
import useSnackbarMessage from "../../../utils/HandleSuccessErrorSnackbars";
import { kybErrorMessage } from "../hooks/useKybLinks";
import { kybStyles } from "../kybStyles";
import KybDialog from "./KybDialog";

interface CreateKybLinkProps {
  onCreated: () => void;
}

type CreateResponse = {
  data?: { id?: string | number; kybId?: string | number };
  id?: string | number;
};

const CreateKybLink: React.FC<CreateKybLinkProps> = ({ onCreated }) => {
  const { handleSuccess, handleError } = useSnackbarMessage();
  const [createOpen, setCreateOpen] = useState(false);
  const [merchantName, setMerchantName] = useState("");
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState("");
  const [createdCopied, setCreatedCopied] = useState(false);
  const [created, setCreated] = useState<{
    id: string | number | null;
    name: string;
  } | null>(null);

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    if (createLoading) return;
    const name = merchantName.trim();
    if (!name) {
      setCreateError("Enter a merchant name.");
      return;
    }
    setCreateLoading(true);
    setCreateError("");
    try {
      const response = await kybService.saveKybData<CreateResponse>(
        { merchantName: name },
        false,
      );
      const id =
        response?.data?.id ?? response?.data?.kybId ?? response?.id ?? null;
      setCreated({ id, name });
      handleSuccess({ message: "KYB link created." });
      setCreatedCopied(false);
      setMerchantName("");
      onCreated();
    } catch (err) {
      const message = kybErrorMessage(
        err,
        "Could not create the link. Please try again.",
      );
      setCreateError(message);
      handleError({ message });
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className={kybStyles.primaryButton}
        onClick={() => {
          setCreated(null);
          setCreateError("");
          setCreateOpen(true);
        }}
      >
        <span className="material-symbols-outlined" aria-hidden="true">
          add
        </span>
        Create link
      </button>
      {createOpen && (
        <KybDialog
          title={created ? "Link created" : "Create KYB link"}
          description={
            created
              ? `Your verification link for ${created.name} is ready.`
              : "Enter a merchant name to create a shareable verification link."
          }
          busy={createLoading}
          onClose={() => setCreateOpen(false)}
        >
          {created ? (
            <>
              <div>
                <span className={kybStyles.fieldLabel}>Verification link</span>
                {created.id != null ? (
                  <div className="flex items-center justify-between gap-[10px] px-[10px] py-2 border border-border-subtle rounded-[6px] bg-bg-base">
                    <span
                      className="truncate text-[12px]"
                      title={`https://kyb.tylt.money/${created.id}`}
                    >
                      https://kyb.tylt.money/{created.id}
                    </span>
                    <button
                      type="button"
                      className={kybStyles.iconButton}
                      aria-label={
                        createdCopied
                          ? "KYB link copied"
                          : "Copy created KYB link"
                      }
                      title={createdCopied ? "Copied" : "Copy link"}
                      onClick={async () => {
                        setCreateError("");
                        try {
                          await navigator.clipboard.writeText(
                            `https://kyb.tylt.money/${created.id}`,
                          );
                          setCreatedCopied(true);
                          handleSuccess({ message: "KYB link copied." });
                        } catch {
                          const message =
                            "Could not copy the link. Please try again.";
                          setCreateError(message);
                          handleError({ message });
                        }
                      }}
                    >
                      <span
                        className="material-symbols-outlined"
                        aria-hidden="true"
                      >
                        {createdCopied ? "check" : "content_copy"}
                      </span>
                    </button>
                  </div>
                ) : (
                  <p className="text-[12px] leading-[1.6]">
                    The record was created. Copy its link from the table once it
                    loads.
                  </p>
                )}
              </div>
              {createError && (
                <p className={kybStyles.formError} role="alert">
                  {createError}
                </p>
              )}
              <div className={kybStyles.dialogFooter}>
                <button
                  type="button"
                  className={kybStyles.secondaryButton}
                  onClick={() => {
                    setCreated(null);
                    setCreateError("");
                  }}
                >
                  Create another
                </button>
                <button
                  type="button"
                  className={kybStyles.primaryButton}
                  data-kyb-initial-focus
                  onClick={() => setCreateOpen(false)}
                >
                  Done
                </button>
              </div>
            </>
          ) : (
            <form onSubmit={handleCreate}>
              <label
                className={kybStyles.fieldLabel}
                htmlFor="kyb-merchant-name"
              >
                Merchant name
              </label>
              <input
                autoFocus
                data-kyb-initial-focus
                id="kyb-merchant-name"
                className={kybStyles.createInput}
                placeholder="e.g. Swift Pay"
                value={merchantName}
                disabled={createLoading}
                aria-invalid={Boolean(createError)}
                aria-describedby={createError ? "kyb-create-error" : undefined}
                onChange={(event) => {
                  setMerchantName(event.target.value);
                  setCreateError("");
                }}
              />
              {createError && (
                <p
                  id="kyb-create-error"
                  className={kybStyles.formError}
                  role="alert"
                >
                  {createError}
                </p>
              )}
              <div className={kybStyles.dialogFooter}>
                <button
                  type="button"
                  className={kybStyles.secondaryButton}
                  disabled={createLoading}
                  onClick={() => setCreateOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={kybStyles.primaryButton}
                  disabled={createLoading || !merchantName.trim()}
                >
                  {createLoading ? "Creating…" : "Create link"}
                </button>
              </div>
            </form>
          )}
        </KybDialog>
      )}
    </>
  );
};

export default CreateKybLink;
